import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { date } from '@/utils/format';
import { Table } from '@/components/Table';
import { Icon } from '@/components/Icon';
import { Badge, type BadgeVariant } from '@/components/Badge';
import { Progress } from '@/components/Progress';
import { Tooltip } from '@/components/Tooltip';
import { EditDevelopmentModal } from '@/pages/Developments/EditDevelopmentModal';
import { ChangePriorityModal } from '@/pages/Developments/ChangePriorityModal';
import { ChangeStatusModal } from '@/pages/Developments/ChangeStatusModal';
import { ChangeAssigneeModal } from '@/pages/Developments/ChangeAssigneeModal';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { developmentApi } from '@/lib/api';
import { Alert } from '@/components/Alert';
import { ListSettings } from '@/components/ListSettings';
import { ListPanel } from '@/components/ListPanel';
import { SavedViews } from '@/components/SavedViews';
import { listViewSchema } from '@/components/listPreferences';
import { useLocalList } from '@/hooks/useLocalList';

const STATUS_VARIANT: Record<string, BadgeVariant> = {
    request:            'info',
    planning:           'secondary',
    development:        'warning',
    testing:            'neutral',
    completed:          'success',
    cancelled:          'danger'
};

const PRIORITY_VARIANT: Record<string, BadgeVariant> = {
    low:     'neutral',
    medium:  'info',
    high:    'warning',
    urgent:  'danger'
};

export interface PriorityConfig {
    arrows: number;
    color: string;
    label: string;
}

export const PRIORITY_CONFIG: Record<string, PriorityConfig> = {
    low:     { arrows: 1, color: 'text-slate-400 dark:text-zinc-500', label: 'text-slate-400 dark:text-zinc-500' },
    medium:  { arrows: 2, color: 'text-blue-500 dark:text-blue-400',  label: 'text-blue-600 dark:text-blue-400' },
    high:    { arrows: 3, color: 'text-amber-500 dark:text-amber-400', label: 'text-amber-600 dark:text-amber-400' },
    urgent:  { arrows: 4, color: 'text-rose-500 dark:text-rose-400',  label: 'text-rose-600 dark:text-rose-400' },
    baja:    { arrows: 1, color: 'text-slate-400 dark:text-zinc-500', label: 'text-slate-400 dark:text-zinc-500' },
    media:   { arrows: 2, color: 'text-blue-500 dark:text-blue-400',  label: 'text-blue-600 dark:text-blue-400' },
    alta:    { arrows: 3, color: 'text-amber-500 dark:text-amber-400', label: 'text-amber-600 dark:text-amber-400' },
    urgente: { arrows: 4, color: 'text-rose-500 dark:text-rose-400',  label: 'text-rose-600 dark:text-rose-400' },
};

interface StepItem {
    id: number;
    is_completed: boolean;
}

export interface DevelopmentRow {
    id: number;
    code: string;
    title: string;
    description: string;
    status: string;
    priority: string;
    design_url?: string | null;
    figma_url?: string | null;
    demo_url?: string | null;
    client?: { name: string };
    assigned_id?: number | null;
    assigned?: { id?: number; name: string; avatar?: string | null; photo?: string | null } | null;
    steps?: StepItem[];
    can_delete?: boolean;
    is_in_phase_one?: boolean;
    has_client_activity?: boolean;
    created_at: string;
    updated_at: string;
}

export interface DevelopmentsTableProps {
    className?: string;
    items?: DevelopmentRow[];
    loading?: boolean;
    search?: string;
    onSearchChange?: (val: string) => void;
    filterElement?: React.ReactNode;
    filters?: React.ReactNode;
    filterFooter?: React.ReactNode;
    emptyText?: string;
    onUpdateItem?: (updated: DevelopmentRow) => void;
    onDeleteItem?: (deletedId: number) => void;
    onRefresh?: () => void;
    viewFilters?: Record<string, string>;
    viewContext?: string;
    onRestoreView?: (filters: Record<string, string>, context: string) => void;
}

export const DevelopmentsTable: React.FC<DevelopmentsTableProps> = ({
    className = '',
    items = [],
    loading = false,
    search,
    onSearchChange,
    filterElement,
    filters,
    filterFooter,
    emptyText,
    onUpdateItem,
    onDeleteItem,
    onRefresh,
    viewFilters,
    viewContext,
    onRestoreView,
}) => {
    const { t } = useTranslation('global');
    const { isIt } = useAuth();
    const { showSuccess, showError } = useToast();
    const navigate = useNavigate();
    const [editTarget, setEditTarget] = useState<DevelopmentRow | null>(null);
    const [priorityTarget, setPriorityTarget] = useState<DevelopmentRow | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<DevelopmentRow | null>(null);
    const [changeStatusTarget, setChangeStatusTarget] = useState<DevelopmentRow | null>(null);
    const [assigneeTarget, setAssigneeTarget] = useState<DevelopmentRow | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const list = useLocalList({ key: 'vigepra_developments', search: search || '', onSearch: value => onSearchChange?.(value), initialSort: { key: 'updated_at', order: 'desc' }, filters: viewFilters, context: viewContext, onRestore: onRestoreView, defaultFilters: { status: '', priority: '', assignee: '' }, defaultContext: 'open' });
    const { preferences, setPreferences } = list;

    const handlePriorityClick = (e: React.MouseEvent<HTMLButtonElement>, row: DevelopmentRow): void => {
        e.stopPropagation();
        setPriorityTarget(row);
    };

    const handlePrioritySuccess = (updated: DevelopmentRow): void => {
        setPriorityTarget(null);
        if(onUpdateItem){
            onUpdateItem(updated);
        }
        else {
            if(onRefresh){
                onRefresh();
            }
            else {
                window.location.reload();
            }
        }
    };

    const handleEditSuccess = (updated?: unknown): void => {
        setEditTarget(null);
        if(onUpdateItem && updated){
            onUpdateItem(updated as DevelopmentRow);
        }
        else {
            if(onRefresh){
                onRefresh();
            }
            else {
                window.location.reload();
            }
        }
    };

    const canDeleteRow = (row: DevelopmentRow): boolean => {
        if(!isIt){
            return false;
        }

        if(typeof row.can_delete === 'boolean'){
            return row.can_delete;
        }

        const inPhaseOne = row.is_in_phase_one ?? (
            !row.steps?.some((s) => s.is_completed)
        );

        return inPhaseOne && !row.has_client_activity;
    };

    const handleConfirmDelete = async (): Promise<void> => {
        if(!deleteTarget || !canDeleteRow(deleteTarget)){
            return;
        }

        setIsDeleting(true);

        try {
            const res = await developmentApi.delete(deleteTarget.id);
            if(res.result){
                showSuccess(t('developments.modal_delete.success'));
                const deletedId = deleteTarget.id;
                setDeleteTarget(null);
                if(onDeleteItem){
                    onDeleteItem(deletedId);
                }
                else {
                    if(onRefresh){
                        onRefresh();
                    }
                    else {
                        window.location.reload();
                    }
                }
            }
            else {
                showError(res.message || t('developments.modal_delete.error'));
            }
        }
        catch (err) {
            showError(err instanceof Error ? err.message : t('developments.modal_delete.error'));
        }
        finally {
            setIsDeleting(false);
        }
    };

    const tableColumns = [
         {
            key: 'code',
            label: t('developments.code'),
            sortable: true,
            render: (row: DevelopmentRow) => (
                <Badge
                    value={row.code}
                />
            ),
        },
        {
            key: 'updated_at',
            label: t('developments.updated_at'),
            sortable: true,
            nowrap: true,
            render: (row: DevelopmentRow) => (
                <span>
                    {date(row.updated_at)}
                </span>
            ),
        },       
        {
            key: 'title',
            label: t('developments.development'),
            sortable: true,
            render: (row: DevelopmentRow) => (
                <div className="flex flex-col py-0.5">
                        <Tooltip title={row.title} placement="top-start">
                            <span className="font-bold line-clamp-1">
                                {row.title}
                            </span>
                        </Tooltip>
                        <span className="text-xs text-slate-500 dark:text-zinc-400 line-clamp-1 mt-0.5">
                            {row.description}
                        </span>                                            
                </div>
            )
        },
        {
            key: 'status',
            label: t('developments.status'),
            sortable: true,
            align: 'center',
            render: (row: DevelopmentRow) => (
                <Badge 
                    variant={STATUS_VARIANT[row.status] ?? 'neutral'}
                    value={t(`statuses.${row.status}`)}
                />
            ),
        },
        {
            key: 'assigned',
            label: t('developments.detail.assigned'),
            sortable: false,
            align: 'center' as const,
            render: (row: DevelopmentRow) => {
                const name = row.assigned?.name;
                const initials = name
                    ? name.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase()
                    : '?';

                return (
                    <Tooltip title={name || t('developments.detail.unassigned')}>
                        <button
                            type="button"
                            className="inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-primary text-xs font-bold text-white transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                            onClick={(event) => {
                                event.stopPropagation();
                                setAssigneeTarget(row);
                            }}
                        >
                            {row.assigned?.avatar || row.assigned?.photo ? (
                                <img className="h-full w-full object-cover" src={row.assigned.avatar || row.assigned.photo || ''} alt={name || ''} />
                            ) : initials}
                        </button>
                    </Tooltip>
                );
            },
        },
        {
            key: 'priority',
            label: t('developments.priority'),
            sortable: true,
            align: 'center',
            render: (row: DevelopmentRow) => {
                const cfg = PRIORITY_CONFIG[row.priority];
                if(!cfg){
                    return null;
                }

                return (
                    <Tooltip title={t(`priorities.${row.priority}`)} placement="top">
                        <button
                            type="button"
                            onClick={(e: React.MouseEvent<HTMLButtonElement>): void => handlePriorityClick(e, row)}
                            className="inline-flex cursor-pointer transition-transform hover:scale-105 active:scale-95 focus:outline-none"
                        >
                            {/*
                            <Badge
                                variant={PRIORITY_VARIANT[row.priority] ?? 'neutral'}
                                size="sm"
                                pill
                                className="inline-flex items-center gap-1.5 font-semibold hover:ring-2 hover:ring-primary/25 dark:hover:ring-white/25 transition-all shadow-2xs"
                            >
                                <span className={`inline-flex items-center gap-0 ${cfg.color}`}>
                                    {Array.from({ length: cfg.arrows }).map((_, i) => (
                                        <Icon key={i} value="arrowUp" size={13} strokeWidth={3} />
                                    ))}
                                </span>
                            </Badge>
                            */}
                            <span className={`inline-flex items-center gap-0 ${cfg.color}`}>
                                {Array.from({ length: cfg.arrows }).map((_, i) => (
                                    <Icon key={i} value="arrowUp" size={16} strokeWidth={3} />
                                ))}
                            </span>
                        </button>
                    </Tooltip>
                );
            },
        },
        {
            key: 'progress',
            label: t('developments.progress'),
            sortable: false,
            render: (row: DevelopmentRow) => {
                const completed = row.steps?.filter((s) => s.is_completed).length || 0;
                const total = row.steps?.length || 8;
                const pct = Math.round((completed / total) * 100);

                return <Progress value={pct} height={12}/>;
            },
        },
        {
            key: 'client',
            label: t('developments.client_label'),
            sortable: true,
            //nowrap: true,
            render: (row: DevelopmentRow) => (
                <span className="">
                    {row.client?.name}
                </span>
            ),
        }
    ];

    const tableActions = [
        {
            label: t('table.share'),
            icon: <Icon value="share2" size={14} className="text-slate-600 dark:text-zinc-300" />,
            onClick: async (row: DevelopmentRow): Promise<void> => {
                const url = `${window.location.origin}/developments/${row.id}`;
                if (navigator.share) {
                    try {
                        await navigator.share({
                            title: row.title,
                            text: row.description || '',
                            url: url,
                        });
                    } catch (error) {
                        // ignore abort error if user cancels sharing
                        console.error('Error sharing', error);
                    }
                }
                else {
                    navigator.clipboard.writeText(url);
                    showToast(t('table.link_copied'), 'success');
                }
            },
        },
        ...(isIt ? [
            {
                label: t('table.edit'),
                icon: <Icon value="pencil" size={14} className="text-slate-600 dark:text-zinc-300" />,
                onClick: (row: DevelopmentRow): void => {
                    setEditTarget(row);
                },
            }
        ] : []),
        ...(isIt ? [
            {
                label: t('developments.modal_status.title_cancel'),
                icon: <Icon value="ban" size={14} className="text-slate-600 dark:text-zinc-300" />,
                show: (row: DevelopmentRow) => row.status !== 'cancelled',
                onClick: (row: DevelopmentRow): void => {
                    setChangeStatusTarget(row);
                },
            },
            {
                label: t('developments.modal_status.btn_reopen'),
                icon: <Icon value="play" size={14} className="text-slate-600 dark:text-zinc-300" />,
                show: (row: DevelopmentRow) => row.status === 'cancelled',
                onClick: (row: DevelopmentRow): void => {
                    setChangeStatusTarget(row);
                },
            }
        ] : []),
        ...(isIt ? [
            {
                label: t('table.delete'),
                icon: <Icon value="trash" size={14} className="text-rose-600" />,
                variant: 'delete' as const,
                onClick: (row: DevelopmentRow): void => {
                    setDeleteTarget(row);
                },
            },
        ] : []),
    ];

    return (
        <div className={className}>
            <Table
                appearance="workspace"
                sort={list.sort}
                onSortChange={list.setSort}
                pagination={list.pagination(items.length)}
                manualPagination={false}
                density={preferences.density}
                columns={tableColumns.filter(column => !preferences.hidden.includes(column.key))}
                data={items}
                actions={tableActions}
                actionsPosition="right"
                actionMenu={true}
                searchable={true}
                filterbox={false}
                searchValue={search}
                onSearchChange={onSearchChange}
                searchPlaceholder={t('developments.search_placeholder')}
                filterElement={(
                    <div className="flex flex-wrap items-center gap-1.5">
                        {(filterElement || filters) && <Button variant="outline" size="sm" icon="filter" aria-expanded={filtersOpen} onClick={() => setFiltersOpen(previous => !previous)}>{t('providers.ui.filters')}</Button>}
                        <SavedViews storageKey="vigepra_development_views" schema={listViewSchema} value={list.view} defaultValue={list.defaultView} onApply={list.apply}
                            configuration={<ListSettings inline columns={tableColumns} value={preferences} onChange={setPreferences} sort={list.sort} onSortChange={list.setSort} />} />
                    </div>
                )}
                filterFooter={filtersOpen ? undefined : filterFooter}
                loading={loading}
                emptyText={emptyText || t('developments.empty_title')}
                paginated={true}
                defaultPageSize={10}
                defaultSortKey="updated_at"
                defaultSortOrder="desc"
                onRowClick={(row: DevelopmentRow) => navigate(`/developments/${row.id}`)}
                filterPanel={<ListPanel isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} title={t('providers.ui.filters')} footer={filtersOpen ? filterFooter : undefined}>
                    <div className="flex flex-wrap gap-3 [&>*]:!w-auto [&>*]:min-w-48 [&>*]:flex-1">{filterElement || filters}</div>
                </ListPanel>}
            />

            <EditDevelopmentModal
                isOpen={editTarget !== null}
                development={editTarget}
                onClose={() => setEditTarget(null)}
                onSuccess={handleEditSuccess}
            />

            <ChangePriorityModal
                isOpen={priorityTarget !== null}
                development={priorityTarget}
                onClose={() => setPriorityTarget(null)}
                onSuccess={handlePrioritySuccess}
            />

            <ChangeAssigneeModal
                isOpen={assigneeTarget !== null}
                development={assigneeTarget}
                onClose={() => setAssigneeTarget(null)}
                onSuccess={(updated) => {
                    if (assigneeTarget && onUpdateItem) {
                        onUpdateItem({ ...assigneeTarget, ...updated });
                    }
                    else {
                        onRefresh?.();
                    }
                    setAssigneeTarget(null);
                }}
            />

            {/* Delete Confirmation / Blocked Modal */}
            {deleteTarget && (
                <Modal
                    isOpen={deleteTarget !== null}
                    onClose={(): void => {
                        if(!isDeleting){
                            setDeleteTarget(null);
                        }
                    }}
                    title={canDeleteRow(deleteTarget) ? t('developments.modal_delete.title') : t('developments.modal_delete_blocked.title')}
                    maxWidth="sm"
                    submitVariant={canDeleteRow(deleteTarget) ? "danger" : undefined}
                    onSubmit={canDeleteRow(deleteTarget) ? handleConfirmDelete : undefined}
                    isSubmitting={isDeleting}
                    submitLabel={canDeleteRow(deleteTarget) ? t('modal.delete') : undefined}
                    submittingLabel={t('modal.deleting')}
                    cancelLabel={canDeleteRow(deleteTarget) ? t('modal.cancel') : t('modal.close')}
                >
                    {!canDeleteRow(deleteTarget) ? (
                        <div className="">
                            <div className="mx-auto mb-2 w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 border border-amber-200 dark:border-amber-900/50">
                                <Icon value="alertTriangle" size={20} />
                            </div>
                            <div className="">
                                <div className="text-center">
                                    <Badge
                                        variant="neutral"
                                        size="md"
                                        pill
                                        className="font-mono font-bold text-xs px-3 py-0.5 rounded-full bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-zinc-200 border border-slate-300/80 dark:border-white/15 shadow-2xs"
                                    >
                                        {deleteTarget.code}
                                    </Badge>
                                </div>
                                <div className="text-center text-sm font-bold text-slate-900 dark:text-white truncate">
                                    {deleteTarget.title}                                    
                                </div>
                                <div className="text-center my-4 text-xs text-slate-600 dark:text-zinc-300 leading-relaxed">
                                    {t('developments.modal_delete_blocked.desc')}
                                </div>
                                <div className="flex flex-col gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                                    {(deleteTarget.is_in_phase_one === false || deleteTarget.steps?.some((s) => s.is_completed)) && (
                                        <Alert 
                                            variant='warning'
                                            icon={<Icon value="clock" size={13} className="shrink-0" />}
                                        >
                                            <span>{t('developments.modal_delete_blocked.reason_phase')}</span>
                                        </Alert>
                                    )}
                                    {deleteTarget.has_client_activity && (
                                        <Alert 
                                            variant='warning'
                                            icon={<Icon value="message" size={13} className="shrink-0" />}
                                        >
                                            <span>{t('developments.modal_delete_blocked.reason_messages')}</span>
                                        </Alert>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4 py-1">
                            <p className="text-center text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                                {t('developments.modal_delete.desc')}
                            </p>

                            <div className="text-center p-3.5 bg-slate-50 dark:bg-dark-subtle/60 rounded-xl border border-slate-200/80 dark:border-white/10 space-y-2">
                                <div>
                                    <Badge
                                        variant="neutral"
                                        size="md"
                                        pill
                                        className="font-mono font-bold text-xs px-3 py-1 rounded-full bg-white dark:bg-white/10 text-slate-700 dark:text-zinc-200 border border-slate-300/80 dark:border-white/15 shadow-2xs"
                                    >
                                        {deleteTarget.code}
                                    </Badge>
                                </div>

                                <div className="text-sm font-bold text-slate-900 dark:text-white">
                                    {deleteTarget.title}
                                </div>

                                {deleteTarget.description && (
                                    <div className="text-xs text-slate-500 dark:text-zinc-400 whitespace-pre-line leading-relaxed max-h-36 overflow-y-auto">
                                        {deleteTarget.description}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </Modal>
            )}

            <ChangeStatusModal
                isOpen={changeStatusTarget !== null}
                onClose={() => setChangeStatusTarget(null)}
                target={changeStatusTarget}
                onSuccess={(newStatus) => {
                    setChangeStatusTarget(null);
                    if(onRefresh) onRefresh();
                }}
            />
        </div>
    );
};

export default DevelopmentsTable;
